import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { T } from '@design/index';
import { useStore } from '../../state/patientProductionStore';
import { patientApi } from '../../api';
import type { MarketplaceProvider } from '../../types';
import { getCoords, onCoordsChanged, type Coords } from '../../utils/patientLocation';
import { DoctorCard, offersOnline } from './DoctorCard';

const card = (extra?: React.CSSProperties): React.CSSProperties => ({
  background: T.white, border: `1px solid ${T.border}`, borderRadius: 14,
  padding: 16, boxShadow: T.shadow, ...extra,
});



function IconVideo({ color, size = 18 }: { color: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="6" width="13" height="12" rx="2" />
      <path d="M15 10l5-3v10l-5-3z" />
    </svg>
  );
}

function IconChat({ color, size = 18 }: { color: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
    </svg>
  );
}

function IconCalendar({ color, size = 14 }: { color: string; size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="18" rx="2" /><path d="M8 2v4" /><path d="M16 2v4" /><path d="M3 10h18" />
    </svg>
  );
}

export default function TelemedicinePage() {
  const navigate = useNavigate();
  const localAppointments = useStore(s => s.appointments);
  const [onlineDoctors, setOnlineDoctors] = useState<MarketplaceProvider[]>([]);
  const [loadingDoctors, setLoadingDoctors] = useState(true);
  /** A failed load is said plainly — it is not "no doctors consult online". */
  const [loadError, setLoadError] = useState(false);
  const [apiTele, setApiTele] = useState<{ id: string; doctorName: string; specialty?: string; date: string; time: string; status: string; type: string }[]>([]);

  // Server appointments are the source of truth (video consultations booked
  // on any device, cancellations included); the local mirror only fills in
  // anything the server list doesn't already carry.
  useEffect(() => {
    let alive = true;
    patientApi.getAppointments()
      .then((rows) => {
        if (!alive) return;
        setApiTele(rows
          .filter((a: any) => (a.consultationType === 'VIDEO') && a.status !== 'CANCELLED')
          .map((a: any) => ({
            id: a.id, doctorName: a.doctorName, specialty: a.speciality ?? a.specialty,
            date: (a.appointedAt || '').slice(0, 10), time: (a.appointedAt || '').slice(11, 16),
            status: a.status, type: 'Telemedicine',
          })));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const seen = new Set(apiTele.map(a => a.id));
  const teleAppointments = [
    ...apiTele,
    ...localAppointments.filter(a => a.type === 'Telemedicine' && a.status !== 'CANCELLED' && !seen.has(a.id)),
  ];

  // Who actually consults online is `consultModes` — what the server derived
  // from the schedules each doctor published. This list used to be filtered on
  // `tags`, a free-text label the provider types: it hid Dr. Anjali Sharma
  // (no tags at all) and Dr. Vikram Singh, both of whom publish video
  // consultations and a video fee, so this screen showed four of the six.
  useEffect(() => {
    let alive = true;
    patientApi.getMarketplaceProviders({ type: 'DOCTOR', limit: 20 })
      .then((res) => {
        if (!alive) return;
        setOnlineDoctors((res.providers ?? []).filter(offersOnline));
        setLoadError(false);
      })
      .catch(() => { if (alive) { setOnlineDoctors([]); setLoadError(true); } })
      .finally(() => { if (alive) setLoadingDoctors(false); });
    return () => { alive = false; };
  }, []);

  // ── Saved doctors and distance, exactly as the directory handles them, so
  //    the card behaves the same on both screens.
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [saveError, setSaveError] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    patientApi.getFavoriteIds()
      .then((ids) => { if (alive) setSavedIds(new Set(ids ?? [])); })
      .catch(() => { if (alive) setSavedIds(new Set()); });
    return () => { alive = false; };
  }, []);

  const toggleSaved = async (p: MarketplaceProvider) => {
    const wasSaved = savedIds.has(p.id);
    setSavedIds((prev) => {
      const next = new Set(prev);
      if (wasSaved) next.delete(p.id); else next.add(p.id);
      return next;
    });
    setSaveError(null);
    try {
      if (wasSaved) await patientApi.removeFavorite(p.id);
      else await patientApi.addFavorite(p.id);
    } catch {
      setSavedIds((prev) => {
        const next = new Set(prev);
        if (wasSaved) next.add(p.id); else next.delete(p.id);
        return next;
      });
      setSaveError('Could not update your saved doctors. Please try again.');
    }
  };

  const [coords, setCoords] = useState<Coords | null>(() => getCoords());
  useEffect(() => onCoordsChanged(setCoords), []);

  return (
    <div style={{ padding: 16, fontFamily: T.font, maxWidth: 1080, margin: '0 auto', paddingBottom: 100 }}>
      {/* Hero Section */}
      <div style={{ textAlign: 'center', marginBottom: 24 }}>
        <div style={{ width: 56, height: 56, borderRadius: 16, background: '#7C3AED12', display: 'grid', placeItems: 'center', margin: '0 auto 12px' }}>
          <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="3" width="20" height="13" rx="2" /><path d="M8 21h8" /><path d="M12 17v4" /><circle cx="12" cy="10" r="3" />
          </svg>
        </div>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: T.text, margin: 0 }}>Telemedicine</h2>
        <p style={{ fontSize: 12, color: T.textSec, marginTop: 4, lineHeight: 1.5 }}>
          Consult verified doctors via video or audio from the comfort of your home. Get prescriptions, follow-ups, and specialist opinions online.
        </p>
      </div>

      {/* ── Upcoming Telemedicine Appointments ── */}
      {teleAppointments.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 15, fontWeight: 800, color: T.text, margin: '0 0 12px' }}>Upcoming Consultations</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {teleAppointments.map(appt => {
              const statusColor = appt.status === 'CONFIRMED' ? '#059669' : appt.status === 'COMPLETED' ? T.navy : '#D97706';
              return (
                <div key={appt.id} style={card({ borderLeft: `3px solid #7C3AED` })}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                    <div style={{ width: 44, height: 44, borderRadius: 12, background: '#7C3AED12', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <IconVideo color="#7C3AED" size={22} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: T.font, fontSize: 14, fontWeight: 700, color: T.text, marginBottom: 2 }}>
                        {appt.doctorName}
                      </div>
                      <div style={{ fontFamily: T.font, fontSize: 11, color: T.textSec, marginBottom: 6 }}>
                        {appt.specialty}
                      </div>
                      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: T.font, fontSize: 11, color: T.textSec }}>
                          <IconCalendar color={T.textTer} size={12} />
                          {appt.date} · {appt.time}
                        </span>
                        <span style={{
                          fontFamily: T.font, fontSize: 9, fontWeight: 700,
                          color: statusColor, background: statusColor + '14',
                          borderRadius: 20, padding: '2px 8px',
                        }}>
                          {appt.status}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Action buttons */}
                  {appt.status !== 'COMPLETED' && (
                    <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                      {seen.has(appt.id) && (
                        <button
                          onClick={() => navigate(`/patient/telemedicine/consult/${encodeURIComponent(appt.id)}`)}
                          style={{
                            flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                            fontFamily: T.font, fontSize: 12, fontWeight: 700, color: '#fff',
                            background: '#7C3AED', border: 'none',
                            borderRadius: 10, padding: '9px 0', cursor: 'pointer',
                          }}
                        >
                          <IconVideo color="#fff" size={15} />
                          Join video consultation
                        </button>
                      )}
                      <button
                        onClick={() => navigate('/patient/my-chat')}
                        style={{
                          flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                          fontFamily: T.font, fontSize: 12, fontWeight: 700, color: T.navy,
                          background: T.navy + '10', border: `1.5px solid ${T.navy}25`,
                          borderRadius: 10, padding: '9px 0', cursor: 'pointer',
                        }}
                      >
                        <IconChat color={T.navy} size={15} />
                        Start Chat
                      </button>
                    </div>
                  )}
                  {appt.status !== 'COMPLETED' && (
                    <p style={{ fontFamily: T.font, fontSize: 10, color: T.textTer, margin: '8px 0 0', lineHeight: 1.5 }}>
                      In-app video is not available yet — your doctor shares the video link (or calls you) at the appointment time. Use chat for messages.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Doctors who consult online ──
          The same card the doctor directory shows, because in the Patient app
          this screen IS that directory with the Telemedicine filter already
          on. Booking from here defaults to the video consultation. */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ fontSize: 15, fontWeight: 800, color: T.text, margin: '0 0 12px' }}>
          Doctors who consult online
          {!loadingDoctors && onlineDoctors.length > 0 && (
            <span style={{ fontSize: 12, fontWeight: 600, color: T.textSec }}> · {onlineDoctors.length}</span>
          )}
        </h3>

        {saveError && (
          <div style={{ fontSize: 11, color: T.warning, marginBottom: 8 }}>{saveError}</div>
        )}

        {loadingDoctors ? (
          <p style={{ fontSize: 12, color: T.textSec, textAlign: 'center', padding: '16px 0' }}>Loading doctors…</p>
        ) : onlineDoctors.length === 0 ? (
          <div style={card({ textAlign: 'center' as const })}>
            <p style={{ fontSize: 12, color: T.textSec, margin: 0 }}>
              {loadError
                ? 'Could not load doctors just now. Please check your connection and try again.'
                : 'No doctor on the network has published online consultations yet. You can still browse every doctor below.'}
            </p>
          </div>
        ) : (
          onlineDoctors.map((doc) => (
            <DoctorCard
              key={doc.id}
              provider={doc}
              coords={coords}
              isSaved={savedIds.has(doc.id)}
              channel="ONLINE"
              onToggleSave={() => void toggleSaved(doc)}
              onViewProfile={() => navigate(`/patient/care-services/doctors/${doc.slug || doc.id}`)}
              onBook={() => navigate(`/patient/care-services/doctors/${doc.slug || doc.id}/book?type=telemedicine`)}
            />
          ))
        )}

        <button
          onClick={() => navigate('/patient/care-services/doctors')}
          style={{
            width: '100%', marginTop: 12, fontFamily: T.font, fontSize: 13, fontWeight: 700,
            color: '#7C3AED', background: '#7C3AED10', border: `1.5px solid #7C3AED30`,
            borderRadius: 12, padding: '12px 0', cursor: 'pointer',
          }}
        >
          Browse All Doctors
        </button>
      </div>

      {/* Quick Links */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
        <button onClick={() => navigate('/patient/my-chat')} style={{ ...card(), display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', width: '100%', textAlign: 'left' as const }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: `${T.navy}12`, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <IconChat color={T.navy} size={22} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>My Consultations</div>
            <div style={{ fontSize: 11, color: T.textTer, marginTop: 2 }}>View chat history and ongoing sessions</div>
          </div>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={T.textTer} strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
        </button>

        <button onClick={() => navigate('/patient/appointments')} style={{ ...card(), display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer', width: '100%', textAlign: 'left' as const }}>
          <div style={{ width: 44, height: 44, borderRadius: 12, background: '#4F46E512', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
            <IconCalendar color="#4F46E5" size={22} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: T.text }}>Upcoming Appointments</div>
            <div style={{ fontSize: 11, color: T.textTer, marginTop: 2 }}>View scheduled telemedicine sessions</div>
          </div>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke={T.textTer} strokeWidth="2"><path d="M9 18l6-6-6-6" /></svg>
        </button>
      </div>

      {/* Safety Disclaimer */}
      <div style={{ padding: 14, background: '#7C3AED08', borderRadius: 12, border: '1px solid #7C3AED15' }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
          <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round" style={{ flexShrink: 0, marginTop: 1 }}>
            <circle cx="12" cy="12" r="10" /><path d="M12 8v4" /><path d="M12 16h.01" />
          </svg>
          <p style={{ fontSize: 10, color: T.textTer, lineHeight: 1.6, margin: 0 }}>
            Telemedicine consultations require a stable internet connection for video/audio calls. These consultations are provided by verified NeoHealthCard™ network doctors. For medical emergencies, call 112 or visit the nearest hospital immediately. Record sharing with the consulting doctor requires your explicit consent.
          </p>
        </div>
      </div>
    </div>
  );
}
